import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { uploadFotoPerfil } from "../../lib/perfil";
import { supabase } from "../../lib/supabase";
import { Camera, Loader2, ArrowLeft, Trash2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { appConfig } from "../../data/mock";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function Perfil() {
  const { user, fotoPerfilUrl, setFotoPerfilUrl, postos, condominios, getById } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading]           = useState(false);
  const [confirmRemover, setConfirmRemover] = useState(false);
  const [removendo, setRemovendo]           = useState(false);

  const [senhaAtual, setSenhaAtual]         = useState("");
  const [novaSenha, setNovaSenha]           = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [salvandoSenha, setSalvandoSenha]   = useState(false);
  const [erroSenha, setErroSenha]           = useState("");
  const [verSenhaAtual, setVerSenhaAtual]   = useState(false);
  const [verNovaSenha, setVerNovaSenha]     = useState(false);
  const [verConfirmar, setVerConfirmar]     = useState(false);

  const posto = getById(postos, user?.postoId);
  const condo = getById(condominios, user?.condominioId);

  const iniciais = user?.nome
    ?.split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("") || "VG";

  const removerFoto = async () => {
    if (!user?.id) return;
    setRemovendo(true);
    try {
      await supabase.storage.from("perfil-vigilantes").remove([`${user.id}.jpg`]);
      await supabase.from("liberty_perfil_vigilante")
        .update({ foto_url: null })
        .eq("vigilante_id", user.id);
      setFotoPerfilUrl(null);
      setConfirmRemover(false);
      toast.success("Foto removida.");
    } catch (err) {
      console.error("[Perfil] remover foto:", err);
      toast.error("Erro ao remover foto. Tente novamente.");
    }
    setRemovendo(false);
  };

  const handleFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    e.target.value = "";
    setUploading(true);
    try {
      const url = await uploadFotoPerfil(file, user.id, user.nome);
      setFotoPerfilUrl(url);
      toast.success("Foto atualizada com sucesso!");
    } catch (err) {
      console.error("[Perfil] upload:", err);
      toast.error("Erro ao salvar foto. Tente novamente.");
    }
    setUploading(false);
  };

  const alterarSenha = async (e) => {
    e?.preventDefault?.();
    setErroSenha("");
    if (!senhaAtual) { setErroSenha("Informe a senha atual."); return; }
    if (!novaSenha || !confirmarSenha) { setErroSenha("Informe e confirme a nova senha."); return; }
    if (novaSenha.length < 4) { setErroSenha("A nova senha precisa ter pelo menos 4 caracteres."); return; }
    if (novaSenha === "0000" || novaSenha === "1234") { setErroSenha("Escolha uma senha diferente da padrão."); return; }
    if (novaSenha !== confirmarSenha) { setErroSenha("As senhas não conferem."); return; }

    setSalvandoSenha(true);
    try {
      const uid = user?.usuarioId;
      if (!uid) { setErroSenha("Sessão inválida. Faça login novamente."); setSalvandoSenha(false); return; }

      const { data: registro } = await supabase
        .from("liberty_pins")
        .select("pin_hash")
        .eq("vigilante_id", uid)
        .maybeSingle();

      const hashAtual = await sha256Hex(senhaAtual);
      const senhaValida = registro
        ? hashAtual === registro.pin_hash
        : senhaAtual === "0000";

      if (!senhaValida) { setErroSenha("Senha atual incorreta."); setSalvandoSenha(false); return; }

      const hashNova = await sha256Hex(novaSenha);
      const { error: saveErr } = await supabase
        .from("liberty_pins")
        .upsert(
          {
            vigilante_id: uid,
            vigilante_nome: user?.nome,
            pin_hash: hashNova,
            pin_plain: novaSenha,
            empresa_id: EMPRESA_ID,
            forcar_troca: false,
          },
          { onConflict: "vigilante_id" },
        );

      if (saveErr) { setErroSenha("Erro ao salvar. Tente novamente."); setSalvandoSenha(false); return; }

      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");
      toast.success("Senha alterada com sucesso!");
    } catch (err) {
      console.error("[Perfil] alterar senha:", err);
      setErroSenha("Erro ao alterar senha. Tente novamente.");
    }
    setSalvandoSenha(false);
  };

  return (
    <MobileLayout title="Meu Perfil" scrollable>
      <div className="flex flex-col items-center gap-5 py-4">
        {/* Avatar grande */}
        <div className="relative">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#333A56] shadow-xl">
            {fotoPerfilUrl ? (
              <img
                src={fotoPerfilUrl}
                alt="foto de perfil"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#333A56] flex items-center justify-center text-4xl font-bold text-white">
                {iniciais}
              </div>
            )}
          </div>
          {uploading && (
            <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Info do vigilante */}
        <div className="text-center">
          <h2 className="font-display font-bold text-xl text-[#333A56] leading-tight">
            {user?.nome}
          </h2>
          <p className="text-sm text-[#64748b] mt-1">
            {posto?.nome || "Posto não informado"} ·{" "}
            <span translate="no">{condo?.nome || appConfig.condominioPadrao}</span>
          </p>
          {user?.matricula && (
            <p className="text-xs text-[#94a3b8] mt-1 font-mono-data">{user.matricula}</p>
          )}
        </div>

        {/* Botão câmera frontal */}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || removendo}
          className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-[#333A56] text-white font-semibold text-sm active:scale-[0.98] transition disabled:opacity-50"
        >
          <Camera className="w-5 h-5" />
          {fotoPerfilUrl ? "Alterar foto" : "Adicionar foto"}
        </button>

        {/* Botão remover foto — só aparece se há foto */}
        {fotoPerfilUrl && !confirmRemover && (
          <button
            type="button"
            onClick={() => setConfirmRemover(true)}
            disabled={uploading || removendo}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-red-200 text-red-500 text-sm font-medium hover:bg-red-50 active:scale-[0.98] transition disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
            Remover foto
          </button>
        )}

        {/* Confirmação de remoção */}
        {confirmRemover && (
          <div className="w-full rounded-2xl border border-red-200 bg-red-50 p-4 flex flex-col gap-3">
            <p className="text-sm text-red-700 font-semibold text-center">
              Tem certeza que deseja remover sua foto?
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={removerFoto}
                disabled={removendo}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-bold text-sm active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {removendo ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Sim, remover
              </button>
              <button
                type="button"
                onClick={() => setConfirmRemover(false)}
                disabled={removendo}
                className="flex-1 py-2.5 rounded-xl bg-white border border-[#e8eaf0] text-[#333A56] font-semibold text-sm active:scale-[0.98] transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={handleFoto}
        />

        <p className="text-xs text-[#94a3b8] text-center px-8 leading-relaxed">
          Use a câmera frontal para uma foto clara, estilo foto de documento.
          A imagem é redimensionada automaticamente.
        </p>

        {/* Seção Alterar Senha */}
        <div className="w-full rounded-2xl border border-[#e8eaf0] bg-white p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-[#333A56]" />
            <span className="font-semibold text-[#333A56] text-sm">Alterar Senha</span>
          </div>

          <form onSubmit={alterarSenha} className="flex flex-col gap-3">
            <div>
              <label className="text-xs text-[#64748b] block mb-1">Senha atual</label>
              <div className="relative">
                <input
                  type={verSenhaAtual ? "text" : "password"}
                  placeholder="Digite a senha atual"
                  value={senhaAtual}
                  onChange={(e) => { setSenhaAtual(e.target.value); setErroSenha(""); }}
                  autoComplete="current-password"
                  className="w-full px-3.5 pr-10 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-sm focus:border-[#333A56] focus:outline-none placeholder:text-gray-400"
                />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setVerSenhaAtual((v) => !v); }} onTouchEnd={(e) => { e.preventDefault(); setVerSenhaAtual((v) => !v); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#333A56] p-1">
                  {verSenhaAtual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#64748b] block mb-1">Nova senha</label>
              <div className="relative">
                <input
                  type={verNovaSenha ? "text" : "password"}
                  placeholder="Digite a nova senha"
                  value={novaSenha}
                  onChange={(e) => { setNovaSenha(e.target.value); setErroSenha(""); }}
                  autoComplete="new-password"
                  className="w-full px-3.5 pr-10 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-sm focus:border-[#333A56] focus:outline-none placeholder:text-gray-400"
                />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setVerNovaSenha((v) => !v); }} onTouchEnd={(e) => { e.preventDefault(); setVerNovaSenha((v) => !v); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#333A56] p-1">
                  {verNovaSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#64748b] block mb-1">Confirmar nova senha</label>
              <div className="relative">
                <input
                  type={verConfirmar ? "text" : "password"}
                  placeholder="Confirme a nova senha"
                  value={confirmarSenha}
                  onChange={(e) => { setConfirmarSenha(e.target.value); setErroSenha(""); }}
                  autoComplete="new-password"
                  className="w-full px-3.5 pr-10 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-sm focus:border-[#333A56] focus:outline-none placeholder:text-gray-400"
                />
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setVerConfirmar((v) => !v); }} onTouchEnd={(e) => { e.preventDefault(); setVerConfirmar((v) => !v); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#333A56] p-1">
                  {verConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {erroSenha && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {erroSenha}
              </div>
            )}

            <button
              type="submit"
              disabled={salvandoSenha}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#333A56] text-white font-semibold text-sm active:scale-[0.98] transition disabled:opacity-50"
            >
              {salvandoSenha ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Salvar nova senha
            </button>
          </form>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mt-2 text-sm text-[#64748b] hover:text-[#333A56] transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
      </div>
    </MobileLayout>
  );
}
